import Phaser from 'phaser'
import { t } from '../../i18n'
import { matchController } from '../../services/JKGame_MatchController'
import { unlockUpTo } from '../../story/JKGame_progress'
import { CHAPTERS, chapterById, findStoryGraphErrors, SPEAKERS, type StoryChapter, type StoryScene as Scene } from '../../story/JKGame_story'
import { type Emotion, porKey } from '../JKGame_assets'
import { playerPortraitSet } from '../JKGame_cosmetics'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { COLORS, FONT, hasFinePointer, makeButton, prefersReducedMotion } from '../JKGame_ui'

interface StoryData {
  chapterId: string
}

export class JKGame_StoryScene extends Phaser.Scene {
  private chapter!: StoryChapter
  private scene_!: Scene
  private lineIdx = 0
  private awaitingChoice = false
  private portrait!: Phaser.GameObjects.Image
  private nameBox!: Phaser.GameObjects.Rectangle
  private nameTxt!: Phaser.GameObjects.Text
  private bodyTxt!: Phaser.GameObjects.Text
  private bodyTween?: Phaser.Tweens.Tween
  private hint!: Phaser.GameObjects.Text
  private hintTween?: Phaser.Tweens.Tween
  private choiceBtns: Phaser.GameObjects.Container[] = []
  private navigating = false // true once a leave begins; blocks a double-fire (finish bound to button + Enter + Space)

  constructor() {
    super('Story')
  }

  create(data: StoryData): void {
    this.navigating = false // reset on (re)entry — Phaser reuses the scene instance across restarts
    const chapter = chapterById(data.chapterId)
    if (!chapter || !chapter.start || !chapter.scenes) {
      this.scene.start('Menu')
      return
    }
    const start = chapter.start
    this.chapter = chapter
    this.cameras.main.setBackgroundColor('#070a10')

    // ambient backdrop
    const W = DESIGN_WIDTH
    const H = DESIGN_HEIGHT
    this.add.rectangle(0, 0, W, H, 0x0b1018).setOrigin(0, 0)
    this.add.rectangle(0, 0, W, 120, 0x0e1626).setOrigin(0, 0).setStrokeStyle(1, COLORS.panelEdge)
    const titleTxt = this.add.text(W / 2, 60, t(this.chapter.titleKey), { fontFamily: FONT, fontSize: '22px', color: COLORS.text, fontStyle: 'bold' }).setOrigin(0.5)
    // Shrink a wide localized chapter title so it never clips the header edges (parity
    // with every other scene's title; also future-proofs longer LiveOps titles).
    const maxTitleW = W - 32
    if (titleTxt.width > maxTitleW) titleTxt.setScale(maxTitleW / titleTxt.width)

    // The player's avatar reflects the equipped cosmetic character (else the built-in 'you' set).
    this.portrait = this.add.image(W / 2, 330, porKey(playerPortraitSet(SPEAKERS.you.set as string), 'plane')).setScale(2.1).setVisible(false)

    // dialogue panel
    this.add.rectangle(0, 612, W, H - 612, 0x10151f, 0.96).setOrigin(0, 0).setStrokeStyle(1, COLORS.panelEdge)
    this.nameBox = this.add.rectangle(24, 596, 180, 34, 0x1b2533).setOrigin(0, 0).setStrokeStyle(1, COLORS.panelEdge)
    this.nameTxt = this.add.text(36, 603, '', { fontFamily: FONT, fontSize: '18px', color: COLORS.text, fontStyle: 'bold' })
    this.bodyTxt = this.add.text(36, 650, '', {
      fontFamily: FONT,
      fontSize: '19px',
      color: '#eef2f7',
      wordWrap: { width: W - 72, useAdvancedWrap: true }, // useAdvancedWrap → breaks CJK (no spaces)
      lineSpacing: 8,
    })
    this.hint = this.add.text(W - 28, H - 40, t('story.tap'), { fontFamily: FONT, fontSize: '14px', color: COLORS.sub }).setOrigin(1, 1)
    // Decorative "tap to continue" pulse; skip under prefers-reduced-motion (hint stays lit).
    if (!prefersReducedMotion()) this.hintTween = this.tweens.add({ targets: this.hint, alpha: 0.3, duration: 650, yoyo: true, repeat: -1 })
    // Desktop-only keyboard hint (hidden on touch), parity with the other scenes. Placed
    // in the gap below the title bar so it never collides with the dialogue or choices.
    if (hasFinePointer()) {
      const kbd = this.add.text(W / 2, 132, t('story.kbd'), { fontFamily: FONT, fontSize: '12px', color: COLORS.sub }).setOrigin(0.5, 0)
      const maxKbdW = W - 32
      if (kbd.width > maxKbdW) kbd.setScale(maxKbdW / kbd.width)
    }

    // tap anywhere advances (ignored while a choice is pending)
    this.add
      .rectangle(0, 0, W, H, 0x000000, 0.001)
      .setOrigin(0, 0)
      .setInteractive()
      .on('pointerdown', () => this.advance())

    // Always-available exit so the player is never trapped in a chapter. Added
    // after the tap layer so input.topOnly routes the tap here, not to advance().
    makeButton(this, 78, 44, {
      width: 124,
      height: 44,
      label: t('story.toMenu'),
      fontSize: 15,
      onTap: () => this.leave('Menu'),
    })

    // Keyboard advance for desktop (Space / Enter); advance() ignores it while a
    // choice is pending. Scene-scoped listeners are cleaned up on shutdown.
    this.input.keyboard?.on('keydown-SPACE', () => this.advance())
    this.input.keyboard?.on('keydown-ENTER', () => this.advance())
    this.input.keyboard?.on('keydown-ESC', () => this.leave('Menu')) // desktop back (same as the exit button)
    // Number keys pick a branch when choices are showing (1-based → index 0..2).
    this.input.keyboard?.on('keydown-ONE', () => this.selectChoice(0))
    this.input.keyboard?.on('keydown-TWO', () => this.selectChoice(1))
    this.input.keyboard?.on('keydown-THREE', () => this.selectChoice(2))

    // Explicit teardown (matches Explore/Combat/Menu): stop the looping hint pulse
    // and the per-line body fade so nothing tweens past scene shutdown.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup())

    // Guard a broken branching graph: a typo'd goto/next/start (which TS can't catch the way
    // it catches textKeys) would otherwise make gotoScene() fall through to endChapter() and
    // SILENTLY complete + unlock the chapter. Surface it as a visible, recoverable state (the
    // always-present "to menu" button) instead — never a silent failure (CLAUDE.md UX rule).
    const graphErrors = findStoryGraphErrors(chapter)
    if (graphErrors.length > 0) {
      if (import.meta.env?.DEV) console.error(`[story] broken chapter graph '${chapter.id}':`, graphErrors)
      this.showBrokenChapter()
      return
    }

    this.gotoScene(start)
  }

  /** A content/authoring error (broken scene reference) reached runtime: show a localized
   *  notice and let the player back out via the exit button. We deliberately do NOT advance
   *  into the chapter or unlock anything — the chapter did not actually complete. */
  private showBrokenChapter(): void {
    this.awaitingChoice = true // block the tap-to-advance layer (advance() early-returns)
    this.hint.setVisible(false)
    this.hintTween?.stop()
    this.portrait.setVisible(false)
    this.nameBox.setVisible(false)
    this.nameTxt.setVisible(false)
    this.bodyTxt.setScale(1).setColor('#eef2f7').setText(t('story.broken'))
    const maxBodyH = DESIGN_HEIGHT - this.bodyTxt.y - 24
    if (this.bodyTxt.height > maxBodyH) this.bodyTxt.setScale(maxBodyH / this.bodyTxt.height)
  }

  private cleanup(): void {
    this.hintTween?.stop()
    this.hintTween = undefined
    this.bodyTween?.stop()
    this.bodyTween = undefined
    this.clearChoices()
  }

  private gotoScene(id: string): void {
    const sc = this.chapter.scenes?.[id]
    if (!sc) {
      this.endChapter()
      return
    }
    this.scene_ = sc
    this.lineIdx = 0
    this.awaitingChoice = false
    this.clearChoices()
    this.renderLine()
  }

  private renderLine(): void {
    const line = this.scene_.lines[this.lineIdx]
    if (!line) {
      // Defensive: a node with no line at this index (e.g. empty `lines`) must
      // not crash — fall through to the node's exit (choices / next / end).
      this.advance()
      return
    }
    const info = line.speaker ? SPEAKERS[line.speaker] : undefined
    if (info?.set) {
      const emo: Emotion = line.emotion ?? 'plane'
      // The protagonist ('you') wears the equipped cosmetic face; everyone else uses their own.
      const set = line.speaker === 'you' ? playerPortraitSet(info.set) : info.set
      this.portrait.setTexture(porKey(set, emo)).setVisible(true)
    } else {
      this.portrait.setVisible(false)
    }
    if (info) {
      this.nameBox.setVisible(true)
      this.nameTxt.setText(t(info.nameKey)).setColor(info.color).setVisible(true)
      // Grow the name plate to hug a long localized name (e.g. en-US) so it never
      // spills past the fixed box; 12px padding each side, never narrower than before.
      this.nameBox.setSize(Math.max(180, this.nameTxt.width + 24), 34)
    } else {
      this.nameBox.setVisible(false)
      this.nameTxt.setVisible(false)
    }
    this.bodyTxt.setScale(1) // reset before measuring (a prior long line may have shrunk it)
    // Broadcast (PA) lines read in the system-blue tint so they're visually
    // distinct from white character dialogue / narration.
    this.bodyTxt.setColor(line.speaker === 'voice' ? SPEAKERS.voice.color : '#eef2f7')
    this.bodyTxt.setText(t(line.textKey))
    // Shrink an over-tall line so it never spills past the dialogue panel's bottom — and
    // stops above the "tap to continue" hint (bottom-right, ~y902 during dialogue) so a very
    // long line's last rows never overlap it. (endChapter hides the hint, so its own fit
    // guard keeps the full height.)
    const maxBodyH = DESIGN_HEIGHT - this.bodyTxt.y - 66
    if (this.bodyTxt.height > maxBodyH) this.bodyTxt.setScale(maxBodyH / this.bodyTxt.height)
    // Short fade-in per line for reading rhythm. Restart cleanly each render so
    // rapid taps never stack tweens; worst case the text just snaps to full alpha.
    // Under prefers-reduced-motion the line appears instantly (no fade) — parity with
    // the gated hint pulse (#68) and the Gacha result reveal; the text is fully legible.
    this.bodyTween?.stop()
    if (prefersReducedMotion()) {
      this.bodyTxt.setAlpha(1)
    } else {
      this.bodyTxt.setAlpha(0)
      this.bodyTween = this.tweens.add({ targets: this.bodyTxt, alpha: 1, duration: 170 })
    }
    this.hint.setVisible(true)
  }

  /** Guarded scene transition — ignores a repeat trigger (exit button, Esc, or a
   *  completion key) once a leave is already underway. */
  private leave(key: string, data?: object): void {
    if (this.navigating) return
    this.navigating = true
    this.scene.start(key, data)
  }

  private advance(): void {
    if (this.awaitingChoice) return
    if (this.lineIdx < this.scene_.lines.length - 1) {
      this.lineIdx++
      this.renderLine()
      return
    }
    // end of scene
    if (this.scene_.choices && this.scene_.choices.length > 0) {
      this.showChoices()
    } else if (this.scene_.next) {
      this.gotoScene(this.scene_.next)
    } else {
      this.endChapter()
    }
  }

  private showChoices(): void {
    this.awaitingChoice = true
    this.hint.setVisible(false)
    const choices = this.scene_.choices ?? []
    const baseY = DESIGN_HEIGHT - 60 - choices.length * 64
    // The question line stays visible above the choices, but with three buttons the stack
    // climbs to ~y682, leaving only ~32px below the body's top (y650). A long localized
    // question — e.g. a 2-line en-US/ja-JP prompt — would otherwise spill down over the top
    // button. Re-fit from full scale to stop above the first button (parity with renderLine's
    // dialogue-path fit and endChapter's button-clearance fit). Short single-line questions
    // are under the limit and unaffected.
    const firstBtnTop = baseY - 26 // buttons are 52px tall, centred on baseY + i*64
    this.bodyTxt.setScale(1)
    const maxBodyH = firstBtnTop - 8 - this.bodyTxt.y
    if (maxBodyH > 0 && this.bodyTxt.height > maxBodyH) this.bodyTxt.setScale(maxBodyH / this.bodyTxt.height)
    choices.forEach((c, i) => {
      this.choiceBtns.push(
        makeButton(this, DESIGN_WIDTH / 2, baseY + i * 64, {
          width: DESIGN_WIDTH - 80,
          height: 52,
          label: `${i + 1}. ${t(c.textKey)}`, // numbered: clarifies the 1/2/3 keyboard shortcut
          fontSize: 18,
          fill: 0x223049,
          onTap: () => this.selectChoice(i),
        }),
      )
    })
  }

  /** Pick a branch by index (tap or number key); ignored unless that choice exists. */
  private selectChoice(i: number): void {
    if (!this.awaitingChoice) return
    const choice = this.scene_.choices?.[i]
    if (choice) this.gotoScene(choice.goto)
  }

  private clearChoices(): void {
    for (const b of this.choiceBtns) b.destroy()
    this.choiceBtns = []
  }

  private endChapter(): void {
    unlockUpTo(this.chapter.index + 1)
    this.clearChoices()
    this.awaitingChoice = true
    // Completion card: a finale gets "The End"; other chapters "Chapter Complete" — then
    // the authored one-line recap (sumKey), so the summary content is surfaced. Reset
    // scale/color (a prior 'voice' line may have tinted the body blue) and fit-to-height.
    const isFinale = this.chapter.index >= Math.max(...CHAPTERS.map((c) => c.index))
    this.bodyTxt.setScale(1).setColor('#eef2f7')
    this.bodyTxt.setText(`${t(isFinale ? 'story.allComplete' : 'story.complete')}\n\n${t(this.chapter.sumKey)}`)
    // Stop the recap ABOVE the finish button so a long localized recap shrinks to fit rather
    // than overlapping it. The button is centred at DESIGN_HEIGHT-110 (height 58), so its top
    // edge is DESIGN_HEIGHT-139; reserve a 16px gap. (The body starts at y650; the dialogue
    // path #184 reserves the hint row instead — here it's the button that must stay clear.)
    const buttonTop = DESIGN_HEIGHT - 110 - 29
    const maxBodyH = buttonTop - 16 - this.bodyTxt.y
    if (this.bodyTxt.height > maxBodyH) this.bodyTxt.setScale(maxBodyH / this.bodyTxt.height)
    this.nameBox.setVisible(false)
    this.nameTxt.setVisible(false)
    this.portrait.setVisible(false)
    this.hint.setVisible(false)
    this.hintTween?.stop() // no need to keep pulsing an invisible hint on the completion screen

    const finish = (): void => {
      // finish is wired to the button AND keydown-ENTER AND keydown-SPACE, so Enter+Space
      // (or key+tap) could fire it twice → two backend sessions and racing scene.starts.
      // The shared `navigating` guard (also used by the exit button / Esc) collapses that.
      if (this.navigating) return
      this.navigating = true
      if (this.chapter.endsWithMatch) {
        const seed = (Date.now() >>> 0) || 1
        matchController.newMatch(seed) // local sim drives the playable match
        void matchController.startBackendMatch(seed) // authoritative session (no-op unless flag on)
        this.scene.start('Explore')
      } else {
        this.scene.start('Menu')
      }
    }
    const label = this.chapter.endsWithMatch ? t('story.enterMatch') : t('story.toMenu')
    makeButton(this, DESIGN_WIDTH / 2, DESIGN_HEIGHT - 110, {
      width: DESIGN_WIDTH - 100,
      height: 58,
      label,
      fontSize: 22,
      fill: this.chapter.endsWithMatch ? COLORS.btnDanger : COLORS.zoneSafe,
      onTap: finish,
    })
    // Keyboard access for the completion button (advance() is a no-op here).
    this.input.keyboard?.once('keydown-ENTER', finish)
    this.input.keyboard?.once('keydown-SPACE', finish)
  }
}
